/**
 * Schéma du contenu d'une expérience (CLAUDE.md §7).
 *
 * Le contenu est **une donnée validée, pas du code**. Il arrive en JSON et
 * traverse ce validateur avant d'atteindre une vue — de la même façon qu'un
 * dataset traverse `core/dataset.ts`. La raison est la même dans les deux
 * cas : ce qui vient d'un fichier n'a pas rencontré le compilateur.
 *
 * Le validateur rassemble **tous** les manquements plutôt que d'abandonner au
 * premier. Corriger un fichier de contenu en dix allers-retours est le meilleur
 * moyen de finir par ne plus le corriger du tout.
 *
 * Ce module vit hors de `src/core/**` : il ne calcule aucune grandeur, il
 * vérifie une forme.
 */

export interface ContentSection {
  readonly title: string;
  readonly paragraphs: readonly string[];
}

export interface ProtocolSection extends ContentSection {
  readonly steps: readonly string[];
}

export interface ContentPlace {
  readonly id: string;
  readonly label: string;
  readonly latitude: number;
  readonly longitude: number;
}

export interface ExperimentContent {
  readonly id: string;
  readonly places: readonly ContentPlace[];
  readonly question: ContentSection;
  readonly protocol: ProtocolSection;
  readonly limits: ContentSection;
  readonly undecided: ContentSection;
}

export type ContentResult =
  | { readonly ok: true; readonly content: ExperimentContent }
  | { readonly ok: false; readonly problems: readonly string[] };

const SECTION_KEYS = ["question", "protocol", "limits", "undecided"] as const;
const TOP_LEVEL_KEYS = new Set<string>(["id", "places", ...SECTION_KEYS]);
/** Un protocole d'une seule étape n'est pas un protocole. */
const MINIMUM_STEPS = 3;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFilledString(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

function checkStrings(
  value: unknown,
  path: string,
  minimum: number,
  problems: string[],
): readonly string[] {
  if (!Array.isArray(value)) {
    problems.push(`${path} : liste attendue.`);
    return [];
  }
  if (value.length < minimum) {
    problems.push(`${path} : ${minimum} entrée(s) au moins, ${value.length} trouvée(s).`);
  }
  const strings: string[] = [];
  value.forEach((entry, index) => {
    if (isFilledString(entry)) {
      strings.push(entry);
    } else {
      problems.push(`${path}[${index}] : texte non vide attendu.`);
    }
  });
  return strings;
}

function checkSection(
  value: unknown,
  path: string,
  problems: string[],
): ContentSection | undefined {
  if (!isRecord(value)) {
    problems.push(`${path} : section attendue.`);
    return undefined;
  }
  if (!isFilledString(value["title"])) {
    problems.push(`${path}.title : titre non vide attendu.`);
  }
  const paragraphs = checkStrings(value["paragraphs"], `${path}.paragraphs`, 1, problems);
  const title = value["title"];
  if (!isFilledString(title)) return undefined;
  return { title, paragraphs };
}

/**
 * Un lieu remarquable. Les bornes sont celles du globe : une latitude de 100°
 * n'existe pas, et une faute de frappe qui la produirait ferait dessiner une
 * scène absurde sans jamais lever d'erreur.
 */
function checkPlaces(value: unknown, problems: string[]): readonly ContentPlace[] {
  // Toutes les expériences n'ont pas de lieux : une chute libre se règle par sa
  // hauteur et sa pesanteur, pas par une latitude. La clé absente est donc
  // licite ; une clé présente et mal formée ne l'est pas.
  if (value === undefined) return [];
  if (!Array.isArray(value)) {
    problems.push("places : liste attendue.");
    return [];
  }

  const places: ContentPlace[] = [];
  const seen = new Set<string>();

  value.forEach((entry, index) => {
    const path = `places[${index}]`;
    if (!isRecord(entry)) {
      problems.push(`${path} : objet attendu.`);
      return;
    }
    const { id, label, latitude, longitude } = entry;
    if (!isFilledString(id)) problems.push(`${path}.id : identifiant non vide attendu.`);
    if (!isFilledString(label)) problems.push(`${path}.label : libellé non vide attendu.`);
    if (typeof latitude !== "number" || !Number.isFinite(latitude) || Math.abs(latitude) > 90) {
      problems.push(`${path}.latitude : nombre entre -90 et 90 attendu.`);
    }
    if (typeof longitude !== "number" || !Number.isFinite(longitude) || Math.abs(longitude) > 180) {
      problems.push(`${path}.longitude : nombre entre -180 et 180 attendu.`);
    }
    if (isFilledString(id)) {
      if (seen.has(id)) problems.push(`${path}.id : « ${id} » est en double.`);
      seen.add(id);
    }
    if (
      isFilledString(id) &&
      isFilledString(label) &&
      typeof latitude === "number" &&
      typeof longitude === "number"
    ) {
      places.push({ id, label, latitude, longitude });
    }
  });

  return places;
}

/**
 * Valide un contenu d'expérience déjà décodé depuis son JSON.
 *
 * Le décodage reste à l'appelant : un JSON mal formé est une panne de fichier,
 * pas un contenu incomplet, et les deux méritent des messages différents.
 */
export function validateExperimentContent(value: unknown): ContentResult {
  const problems: string[] = [];

  if (!isRecord(value)) {
    return { ok: false, problems: ["Le contenu doit être un objet."] };
  }

  for (const key of Object.keys(value)) {
    if (!TOP_LEVEL_KEYS.has(key)) {
      problems.push(`Clé inconnue : « ${key} ».`);
    }
  }

  if (!isFilledString(value["id"])) {
    problems.push("id : identifiant non vide attendu.");
  }

  const sections = new Map<string, ContentSection>();
  for (const key of SECTION_KEYS) {
    const section = checkSection(value[key], key, problems);
    if (section !== undefined) sections.set(key, section);
  }

  const places = checkPlaces(value["places"], problems);

  const protocolValue = value["protocol"];
  const steps = isRecord(protocolValue)
    ? checkStrings(protocolValue["steps"], "protocol.steps", MINIMUM_STEPS, problems)
    : [];

  if (problems.length > 0) return { ok: false, problems };

  const question = sections.get("question");
  const protocol = sections.get("protocol");
  const limits = sections.get("limits");
  const undecided = sections.get("undecided");
  const id = value["id"];

  if (
    question === undefined ||
    protocol === undefined ||
    limits === undefined ||
    undecided === undefined ||
    !isFilledString(id)
  ) {
    // Inatteignable : `problems` serait non vide. Le compilateur ne peut pas
    // le savoir, et une assertion mensongère coûterait plus cher que ceci.
    return { ok: false, problems: ["Contenu incomplet."] };
  }

  return {
    ok: true,
    content: { id, places, question, protocol: { ...protocol, steps }, limits, undecided },
  };
}

/** Décode puis valide. Le chemin unique par lequel un contenu entre dans la page. */
export function loadExperimentContent(json: string): ContentResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    return { ok: false, problems: [`JSON illisible : ${detail}`] };
  }
  return validateExperimentContent(parsed);
}
